import {BorgoLoginResult, CodeResponse} from '@sparbanken-syd/borgo-types'
import {LocalStorageService} from './local-storage.service'
import {BehaviorSubject, map, NEVER, Observable, of, switchMap} from 'rxjs'
import {environment} from '../../environments/environment'
import {HttpClient, HttpHeaders} from '@angular/common/http'
import {inject} from '@angular/core'


export interface IBorgoLoginResultExt extends BorgoLoginResult {
  /**
   * If logged in as admin...
   */
  adminToken?: string

  /**
   * Sub of user if impersonated
   */
  sub?: string
}

const getEmptyLogin = (): IBorgoLoginResultExt => ({
  token: '',
  refreshToken: '',
  expires: Date.now() - 1001,
  sessionId: ''
})

/**
 * Was intended to be extended by admin, but only
 * User service do atm.
 */
export class RequestService {

  /**
   * Keep this public so that others can react when there are
   * a new valid token to work with.
   */
  public token$ = new BehaviorSubject<IBorgoLoginResultExt | null>(null)

  public adminToken: string | undefined

  /**
   * The session id from the BE
   */
  public userId = ''

  protected borgoAccessToken: IBorgoLoginResultExt = getEmptyLogin()

  private sub: string | undefined

  private readonly borgoTokenStorage: LocalStorageService<IBorgoLoginResultExt | null>

  protected http = inject(HttpClient)

  constructor(tokenName: string) {
    this.borgoTokenStorage = new LocalStorageService<IBorgoLoginResultExt | null>(tokenName, this.token$)
    this.checkBorgoToken()
  }

  public impersonate(sub: string): Observable<string> {
    this.sub = sub
    const url = `${environment.apiUrl}/impersonate`
    const headers = new HttpHeaders({'Authorization': `Bearer ${this.adminToken}`})
    return this.http.put<CodeResponse>(url, {sub, scope: 'LOAN'}, {headers}).pipe(
      map(res => {
        this.borgoAccessToken = {
          token: res.accessToken!,
          expires: res.expiry!,
          sessionId: res.userId,
          refreshToken: res.refreshToken!,
          adminToken: this.adminToken,
          sub
        }
        this.userId = this.borgoAccessToken.sessionId

        this.token$.next(this.borgoAccessToken)
        return res.accessToken!
      })
    )
  }

  /**
   * Checks auth and return the type T using GET
   * @param path - Exactly what you want, _including_ the leading / e.g. /users
   */
  protected getBorgo<T>(path: string): Observable<T> {
    const url = `${environment.apiUrl}${path}`
    return this.checkLogin().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({'X-SPB-Borgo-Token': token})
        return this.http.get<T>(`${url}`, {headers}).pipe()
      })
    )
  }

  /**
   * Delete something with Borgo
   * @param path - Exactly what you want, _including_ the leading / e.g. /users
   */
  protected deleteBorgo(path: string): Observable<void> {
    const url = `${environment.apiUrl}${path}`
    return this.checkLogin().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({'X-SPB-Borgo-Token': token})
        return this.http.delete<void>(`${url}`, {headers}).pipe()
      })
    )
  }

  /**
   * Checks auth and return the type T using PUT sending the data D
   * @param path - Exactly what you want, _including_ the leading / e.g. /users
   * @param data - Anything you can send as a JSON payload
   * @private
   */
  protected putBorgo<T, D>(path: string, data: D): Observable<T> {
    const url = `${environment.apiUrl}${path}`
    return this.checkLogin().pipe(
      switchMap(token => {
        const headers = new HttpHeaders({'X-SPB-Borgo-Token': token})
        return this.http.put<T>(`${url}`, data, {headers}).pipe()
      })
    )
  }

  /**
   * Dummy method implemented by subclass
   */
  protected refresh(): Observable<IBorgoLoginResultExt> {
    return of(this.borgoAccessToken)
  }

  protected reset(): void {
    this.borgoTokenStorage.clear()
    this.borgoAccessToken = getEmptyLogin()
    this.token$.next(null)
  }

  /**
   * If we have a stored token we pick it up.
   */
  protected checkBorgoToken(): void {
    this.borgoAccessToken = this.borgoTokenStorage.get() || getEmptyLogin()
    this.adminToken = this.borgoAccessToken.adminToken
    this.sub = this.borgoAccessToken.sub
    // If token still valid send it to our token$
    this.checkLogin().subscribe({
      next: () => this.token$.next(this.borgoAccessToken)
    })
  }

  protected checkLogin(): Observable<string> {
    // If the token has more than 5 seconds to live, just use the
    // existing token.
    if (this.borgoAccessToken.expires - 5000 > Date.now()) {
      return of(this.borgoAccessToken.token)
    }

    if (this.sub && this.adminToken) {
      return this.impersonate(this.sub)
    }
    // Otherwise if we do not have a refresh token
    // We simply stop here.
    if (!this.borgoAccessToken.refreshToken) {
      return NEVER
    }
    // Otherwise, create a new one
    return this.refresh().pipe(
      map(result => {
        this.borgoAccessToken = result
        this.token$.next(result)
        return result.token
      })
    )
  }
}
